import { useQuery } from '@tanstack/react-query';
import clsx from 'clsx';
import { memo, useMemo } from 'react';

import useMatch from '../../MatchUnit/useMatch';
import { getMatchFormGuideQuery } from '../../queries/cards';
import { useTranslateFn } from '../../Translate';
import VsTeams from '../VsTeams';

import { FormGuideMatches } from './FormGuideMatches';
import { useNumberOfMatches } from './hooks';
import { trackingData } from './utils';

import type { Match } from '@shared-types/match.types';

import * as classes from './styles.module.scss';

const FormGuide = ({
  options,
  options: { extendedView = false, disableTeamLink = false },
}: {
  options: { match: Match; extendedView?: boolean; disableTeamLink?: boolean };
}) => {
  const match = useMatch(options.match);
  const t = useTranslateFn();

  const enableLink = !disableTeamLink;

  const { numberOfMatches } = useNumberOfMatches(extendedView);
  const { data } = useQuery(getMatchFormGuideQuery(match.id, numberOfMatches));

  const homeMatches = useMemo(
    () => data?.find((fg) => fg.team.id === match.homeTeam.id)?.formguide ?? [],
    [data, match.homeTeam.id],
  );
  const awayMatches = useMemo(
    () => data?.find((fg) => fg.team.id === match.awayTeam.id)?.formguide ?? [],
    [data, match.awayTeam.id],
  );

  return (
    <div
      className={clsx({ [classes.extended]: extendedView, 'card-match': !extendedView })}
      data-tracking={JSON.stringify(trackingData)}
    >
      <h3 className={clsx(classes.title, 'card-match-title')}>{t('form_guide')}</h3>
      {extendedView && (
        <div className={classes.subtitle}>
          {t('last_n_matches', { replace: `${Math.max(homeMatches.length, awayMatches.length)}` })}
        </div>
      )}
      <VsTeams
        className={classes.vsTeam}
        classNameHome={classes.home}
        classNameAway={classes.away}
        match={match}
        withDivider={!extendedView}
        invertSecondTeam={!extendedView}
        enableLink={enableLink}
      />

      <div className={classes.teamWrap}>
        <div className={classes.home}>
          <FormGuideMatches matches={homeMatches} team={match.homeTeam} extendedView={extendedView} />
        </div>
        <div className={classes.away}>
          <FormGuideMatches matches={awayMatches} team={match.awayTeam} extendedView={extendedView} />
        </div>
      </div>
    </div>
  );
};

export default memo(FormGuide);
